import { useEffect, useRef, useState } from "react";
import { useAreaZoom } from "../hooks/useAreaZoom";
import { api } from "../lib/api";
import { seoulFilenameDate } from "../lib/time";
import { MediaDebugOverlay, type MediaDebugStats } from "./MediaDebugOverlay";

interface LiveCredentials { token: string; whepUrl: string; audioAllowed?: boolean }
interface LiveStatsSample { peer: RTCPeerConnection; timestamp: number; bytesReceived: number; framesDecoded: number }
interface Props { whepUrl?: string; token?: string; cameraId: string; cameraName: string; canSnapshot: boolean; canAudio: boolean; audioActive: boolean; onAudioToggle: (cameraId: string, enabled: boolean) => void; minimal?: boolean; autoReconnect?: boolean; showDebug?: boolean }

export function WhepPlayer({ whepUrl, token, cameraId, cameraName, canSnapshot, canAudio, audioActive, onAudioToggle, minimal = false, autoReconnect = false, showDebug = false }: Props) {
  const tileRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const liveStatsSampleRef = useRef<LiveStatsSample | null>(null);
  const reconnectCountRef = useRef(0);
  const [state, setState] = useState("연결 중");
  const [volume, setVolume] = useState(1);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [connectionRevision, setConnectionRevision] = useState(0);
  const [audioAllowed, setAudioAllowed] = useState(canAudio);
  const [debugStats, setDebugStats] = useState<MediaDebugStats | null>(null);
  const zoom = useAreaZoom();

  useEffect(() => setAudioAllowed(canAudio), [canAudio]);

  useEffect(() => {
    let disposed = false;
    let controller: AbortController | null = null;
    let peer: RTCPeerConnection | null = null;
    let resourceUrl: string | null = null;
    let resourceToken = token ?? "";
    let retryTimer: number | undefined;
    let disconnectedTimer: number | undefined;
    let handshakeTimer: number | undefined;
    let connectionTimer: number | undefined;

    const clearTimer = (timer: number | undefined) => { if (timer !== undefined) window.clearTimeout(timer); };
    const queueReconnect = (delay?: number) => {
      if (!autoReconnect || disposed || retryTimer !== undefined) return;
      const attempt = reconnectCountRef.current;
      reconnectCountRef.current += 1;
      const retryDelay = delay ?? Math.min(30_000, 2_000 * (2 ** Math.min(attempt, 4)));
      setState("재연결 대기");
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        if (!disposed) setConnectionRevision((current) => current + 1);
      }, retryDelay);
    };

    void (async () => {
      try {
        setState(connectionRevision ? "재연결 중" : "연결 중");
        const credentials: LiveCredentials = connectionRevision === 0 && token && whepUrl
          ? { token, whepUrl, audioAllowed: canAudio }
          : await api<LiveCredentials>(`/api/cameras/${cameraId}/live-token`, { method: "POST" });
        if (disposed) return;
        resourceToken = credentials.token;
        const receiveAudio = canAudio && credentials.audioAllowed !== false;
        setAudioAllowed(receiveAudio);
        const currentController = new AbortController();
        controller = currentController;
        const currentPeer = new RTCPeerConnection();
        peer = currentPeer;
        peerRef.current = currentPeer;
        currentPeer.addTransceiver("video", { direction: "recvonly" });
        if (receiveAudio) currentPeer.addTransceiver("audio", { direction: "recvonly" });
        currentPeer.ontrack = (event) => {
          if (videoRef.current) videoRef.current.srcObject = event.streams[0] ?? new MediaStream([event.track]);
        };
        currentPeer.onconnectionstatechange = () => {
          if (disposed) return;
          const connectionState = currentPeer.connectionState;
          setState(connectionLabel(connectionState));
          if (connectionState === "connected") {
            reconnectCountRef.current = 0;
            clearTimer(disconnectedTimer);
            clearTimer(connectionTimer);
            disconnectedTimer = undefined;
            connectionTimer = undefined;
          } else if (connectionState === "disconnected" && disconnectedTimer === undefined) {
            disconnectedTimer = window.setTimeout(() => queueReconnect(0), 5_000);
          } else if (connectionState === "failed" || connectionState === "closed") {
            queueReconnect();
          }
        };
        handshakeTimer = window.setTimeout(() => currentController.abort(), 15_000);
        const offer = await currentPeer.createOffer();
        await currentPeer.setLocalDescription(offer);
        await waitForIce(currentPeer);
        const response = await fetch(sameOriginUrl(credentials.whepUrl), {
          method: "POST", signal: currentController.signal,
          headers: { "content-type": "application/sdp", authorization: `Bearer ${credentials.token}` },
          body: currentPeer.localDescription?.sdp
        });
        if (!response.ok) throw new Error(`WHEP ${response.status}`);
        const location = response.headers.get("location");
        if (location) resourceUrl = sameOriginUrl(location);
        await currentPeer.setRemoteDescription({ type: "answer", sdp: await response.text() });
        clearTimer(handshakeTimer);
        handshakeTimer = undefined;
        connectionTimer = window.setTimeout(() => {
          if (currentPeer.connectionState !== "connected") queueReconnect(0);
        }, 15_000);
      } catch (error) {
        if (!disposed) {
          setState(error instanceof DOMException && error.name === "AbortError" ? "연결 시간 초과" : error instanceof Error ? error.message : "연결 실패");
          queueReconnect();
        }
      }
    })();

    return () => {
      disposed = true;
      clearTimer(retryTimer);
      clearTimer(disconnectedTimer);
      clearTimer(handshakeTimer);
      clearTimer(connectionTimer);
      controller?.abort();
      if (peer) {
        if (peerRef.current === peer) peerRef.current = null;
        peer.onconnectionstatechange = null;
        peer.close();
      }
      if (resourceUrl && resourceToken) void fetch(resourceUrl, { method: "DELETE", headers: { authorization: `Bearer ${resourceToken}` } }).catch(() => undefined);
    };
  }, [whepUrl, token, cameraId, canAudio, autoReconnect, connectionRevision]);

  useEffect(() => {
    if (!showDebug) {
      liveStatsSampleRef.current = null;
      setDebugStats(null);
      return;
    }
    let disposed = false;
    let collecting = false;
    const collect = async () => {
      const peer = peerRef.current;
      if (!peer || collecting) return;
      collecting = true;
      try {
        const report = await peer.getStats();
        let inbound: RtcVideoInboundStats | undefined;
        report.forEach((entry) => {
          const candidate = entry as unknown as RtcVideoInboundStats;
          if (!inbound && candidate.type === "inbound-rtp" && (candidate.kind === "video" || candidate.mediaType === "video")) inbound = candidate;
        });
        if (!inbound || disposed) return;
        const now = Number(inbound.timestamp ?? performance.now());
        const bytesReceived = Number(inbound.bytesReceived ?? 0);
        const framesDecoded = Number(inbound.framesDecoded ?? 0);
        const previous = liveStatsSampleRef.current;
        const elapsed = previous?.peer === peer ? (now - previous.timestamp) / 1000 : 0;
        const measuredBitrate = elapsed > 0 ? Math.max(0, (bytesReceived - previous!.bytesReceived) * 8 / elapsed) : null;
        const measuredFps = elapsed > 0 ? Math.max(0, (framesDecoded - previous!.framesDecoded) / elapsed) : null;
        const codec = inbound.codecId ? report.get(inbound.codecId) as unknown as { mimeType?: string } | undefined : undefined;
        const video = videoRef.current;
        liveStatsSampleRef.current = { peer, timestamp: now, bytesReceived, framesDecoded };
        setDebugStats({
          source: "LIVE",
          codec: codec?.mimeType?.split("/").pop()?.toUpperCase() ?? null,
          width: Number(inbound.frameWidth ?? video?.videoWidth ?? 0) || null,
          height: Number(inbound.frameHeight ?? video?.videoHeight ?? 0) || null,
          fps: Number.isFinite(inbound.framesPerSecond) ? Number(inbound.framesPerSecond) : measuredFps,
          bitrate: measuredBitrate,
          lostPackets: Number.isFinite(inbound.packetsLost) ? Number(inbound.packetsLost) : null
        });
      } catch {
        // A peer can disappear between interval ticks while the independent reconnect loop replaces it.
      } finally {
        collecting = false;
      }
    };
    void collect();
    const timer = window.setInterval(() => void collect(), 2_000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      liveStatsSampleRef.current = null;
    };
  }, [cameraId, showDebug]);

  const snapshot = async () => {
    setSnapshotBusy(true);
    try {
      const response = await fetch(`/api/cameras/${cameraId}/snapshot?format=jpeg`, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`화면 저장 실패 (${response.status})`);
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.download = `${cameraName}-${seoulFilenameDate(new Date())}.jpg`; link.href = url; link.click();
      URL.revokeObjectURL(url);
    } catch {
      setState("화면 저장 실패");
    } finally { setSnapshotBusy(false); }
  };

  const changeVolume = (next: number) => { setVolume(next); if (videoRef.current) videoRef.current.volume = next; };
  return <div className={`camera-tile${minimal ? " camera-preview-only" : " zoom-surface"}`} ref={tileRef} aria-label={minimal ? `${cameraName} 화면 미리보기` : undefined} {...(minimal ? {} : zoom.surfaceProps)}>
    <video ref={videoRef} autoPlay playsInline muted={!canAudio || !audioActive} style={minimal ? undefined : zoom.videoStyle} />
    {!minimal && zoom.selection && <span className="zoom-selection" style={selectionStyle(zoom.selection)} />}
    {!minimal && <><div className="camera-overlay top"><span>{cameraName}</span><span className={state === "실시간" ? "text-success" : "text-warning"}>● {state}</span></div>
    {showDebug && <MediaDebugOverlay stats={debugStats ?? { source: "LIVE" }} />}
    <div className="camera-actions">
      {audioAllowed&&<>{audioActive&&<input className="camera-volume" type="range" min="0" max="1" step="0.05" value={volume} onChange={(event)=>changeVolume(Number(event.target.value))} aria-label="음량"/>}<button className="btn btn-sm btn-dark" onClick={() => onAudioToggle(cameraId, !audioActive)} aria-label={audioActive ? "오디오 끄기" : "이 카메라 오디오 켜기"}><i className={`bi bi-volume-${audioActive ? "up" : "mute"}`} /></button></>}
      {canSnapshot&&<button className="btn btn-sm btn-dark" disabled={snapshotBusy} onClick={()=>void snapshot()} aria-label="현재 화면 저장"><i className="bi bi-camera" /></button>}
      <button className="btn btn-sm btn-dark" onClick={() => tileRef.current?.requestFullscreen()} aria-label="전체화면"><i className="bi bi-fullscreen" /></button>
    </div></>}
  </div>;
}

interface RtcVideoInboundStats {
  type: string;
  kind?: string;
  mediaType?: string;
  timestamp?: number;
  bytesReceived?: number;
  framesDecoded?: number;
  framesPerSecond?: number;
  frameWidth?: number;
  frameHeight?: number;
  packetsLost?: number;
  codecId?: string;
}

function selectionStyle(selection: { left: number; top: number; width: number; height: number }) {
  return { left: `${selection.left * 100}%`, top: `${selection.top * 100}%`, width: `${selection.width * 100}%`, height: `${selection.height * 100}%` };
}

function connectionLabel(state: RTCPeerConnectionState): string {
  return ({ connected: "실시간", connecting: "연결 중", disconnected: "연결 끊김", failed: "연결 실패", closed: "종료됨", new: "연결 준비" } as const)[state];
}

function waitForIce(peer: RTCPeerConnection): Promise<void> {
  if (peer.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const handler = () => { if (peer.iceGatheringState === "complete") { peer.removeEventListener("icegatheringstatechange", handler); resolve(); } };
    peer.addEventListener("icegatheringstatechange", handler);
    setTimeout(resolve, 3000);
  });
}

function sameOriginUrl(value: string): string {
  const parsed = new URL(value, window.location.origin);
  return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
}
